import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, UserRound, Send, RefreshCw } from "lucide-react";

/** Every WhatsApp conversation Maya has handled, and a way for a human to step in. */
export const WhatsAppThreads = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [reply, setReply] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        api.get("/whatsapp/conversations"), api.get("/whatsapp/status"),
      ]);
      setData(t.data); setStatus(s.data);
    } catch (e) { console.error("Couldn't load WhatsApp threads:", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async (phone) => {
    setBusy(phone);
    try {
      await api.post("/whatsapp/reply", { to: phone, body: reply[phone] });
      toast.success("Sent on WhatsApp");
      setReply({ ...reply, [phone]: "" });
      load();
    } catch (e) {
      toast.error("Couldn't send", { description: e?.response?.data?.detail });
    } finally { setBusy(""); }
  };

  return (
    <Card className="p-5 shadow-premium" data-testid="admin-whatsapp-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <MessageCircle className="h-4 w-4" />WhatsApp conversations
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-lg leading-relaxed">
            {status?.note}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="admin-whatsapp-refresh">
          <RefreshCw className="h-4 w-4 me-1.5" />Refresh
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4">
        {[["Threads", status?.conversations ?? 0], ["Messages", status?.messages ?? 0],
          ["Waiting for a person", status?.needs_human ?? 0]].map(([k, v]) => (
          <div key={k} className="rounded-xl bg-secondary p-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</div>
            <div className="numeric text-xl font-bold mt-0.5">{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {(data?.threads || []).map((th) => (
          <div key={th.phone} className="rounded-xl border p-3" data-testid={`whatsapp-thread-${th.phone}`}>
            <div className="flex items-center gap-2">
              <UserRound className="h-4 w-4 text-muted-foreground" />
              <span className="numeric text-sm font-semibold">{th.phone}</span>
              {th.needs_human && (
                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                  Needs a person
                </span>
              )}
            </div>
            <div className="mt-2 space-y-1.5 text-xs">
              {th.messages.slice(0, 4).map((m, i) => (
                <div key={`${th.phone}-${i}`} className="space-y-1">
                  {m.body && <div className="rounded-lg bg-secondary px-2.5 py-1.5">{m.body}</div>}
                  {m.reply && <div className="rounded-lg bg-emerald-500/10 px-2.5 py-1.5">{m.reply}</div>}
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2.5">
              <Input value={reply[th.phone] || ""} placeholder="Reply as a person…"
                onChange={(e) => setReply({ ...reply, [th.phone]: e.target.value })}
                data-testid={`whatsapp-reply-input-${th.phone}`} />
              <Button size="sm" disabled={busy === th.phone || !(reply[th.phone] || "").trim()}
                onClick={() => send(th.phone)} data-testid={`whatsapp-reply-send-${th.phone}`}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {!data?.threads?.length && (
          <p className="text-xs text-muted-foreground" data-testid="admin-whatsapp-empty">
            No WhatsApp conversations yet.
          </p>
        )}
      </div>
    </Card>
  );
};
